import { useMutation, useQueryClient } from '@tanstack/react-query'
import { getActions } from '@/infra/api/generated/actions/actions'
import type { ActionChoiceDto } from '@/infra/api/generated/model'

const { createCustomActions, patchCustomAction, deleteCustomAction } = getActions()

export const getActionChoicesQueryKey = (diagnosisId: string, questionId: string) => [
  'action-choices',
  diagnosisId,
  questionId,
]

export const useCustomActions = (diagnosisId: string, questionId: string) => {
  const queryClient = useQueryClient()
  const refreshChoices = () =>
    queryClient.invalidateQueries({ queryKey: getActionChoicesQueryKey(diagnosisId, questionId) })

  const { mutate: create } = useMutation({ mutationFn: createCustomActions })
  const { mutate: update } = useMutation({ mutationFn: patchCustomAction })
  const { mutate: remove } = useMutation({ mutationFn: deleteCustomAction })

  const createAction = (label: string, onCreated: (action: ActionChoiceDto) => void) =>
    create(
      { diagnosisId, questionId, label },
      {
        onSuccess: (data) => {
          void refreshChoices()
          if (data) onCreated({ id: data.id, label: data.label, isCustom: true })
        },
      }
    )

  const updateAction = (actionId: string, label: string) =>
    update({ actionId, label }, { onSuccess: () => void refreshChoices() })

  const deleteAction = (actionId: string, onDeleted: () => void) =>
    remove(
      { actionId },
      {
        onSuccess: () => {
          onDeleted()
          void refreshChoices()
        },
      }
    )

  return { createAction, updateAction, deleteAction }
}
